import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Line } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const SemanticChangeCalculator = () => {
  const [word, setWord] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setWord(e.target.value);
    setError('');
  };

  const analyzeSemanticChange = () => {
    if (!word.trim()) {
      setError('Please enter a word');
      return;
    }

    // Generate random data for any word
    const generateRandomData = (word) => {
      const startYear = 1400;
      const endYear = 2020;
      const numPoints = Math.floor(Math.random() * 6) + 3; // 3-8 points
      const points = [];

      for (let i = 0; i < numPoints; i++) {
        const year = startYear + Math.floor((endYear - startYear) / (numPoints - 1) * i);
        const usage = Math.floor(Math.random() * 80) + 20; // 20-100
        
        // Generate a random meaning based on the word
        const meanings = [
          `related to ${word}`,
          `${word} in a broader sense`,
          `${word} with modern connotations`,
          `evolved meaning of ${word}`,
          `${word} in contemporary usage`,
          `expanded definition of ${word}`,
          `${word} in specialized contexts`,
          `inclusive meaning of ${word}`
        ];
        
        const meaning = meanings[Math.floor(Math.random() * meanings.length)];
        
        points.push({ year, meaning, usage });
      }
      
      return points;
    };

    const wordData = generateRandomData(word.toLowerCase());

    const usageData = wordData.map(item => ({
      year: item.year,
      usage: item.usage
    }));

    const meaningData = wordData.map(item => ({
      year: item.year,
      meaning: item.meaning
    }));

    setResults({
      usageData,
      meaningData
    });

    setChartData({
      line: {
        labels: usageData.map(item => item.year),
        datasets: [{
          label: 'Usage Frequency',
          data: usageData.map(item => item.usage),
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
          fill: false
        }]
      },
      bar: {
        labels: meaningData.map(item => item.year),
        datasets: [{
          label: 'Meaning Evolution',
          data: meaningData.map(item => item.meaning),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderWidth: 1
        }]
      }
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Semantic Change Calculator | Analyze Word Meaning Evolution</title>
        <meta name="description" content="Analyze how word meanings have changed over time with our Semantic Change Calculator. Explore usage frequency and semantic evolution through interactive charts." />
        <meta name="keywords" content="semantic change calculator, word evolution, meaning change, language history, linguistic analysis" />
        <meta property="og:title" content="Semantic Change Calculator | Analyze Word Meaning Evolution" />
        <meta property="og:description" content="Analyze how word meanings have changed over time with our Semantic Change Calculator. Explore usage frequency and semantic evolution through interactive charts." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/semantic-change-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔄</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Semantic Change Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Analyze Word Meaning Evolution</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator analyzes how word meanings have changed over time, showing both usage frequency and semantic evolution.
        </p>
      </div>

      <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="word">
            Enter a Word:
          </label>
          <input
            type="text"
            id="word"
            value={word}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter a word to analyze"
          />
        </div>

        <button
          onClick={analyzeSemanticChange}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Analyze Semantic Change
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Usage Frequency Over Time</h4>
                <div className="h-96">
                  <Line
                    data={chartData.line}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          type: 'linear',
                          title: {
                            display: true,
                            text: 'Year'
                          }
                        },
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Usage Frequency'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Word Usage Over Time'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Meaning Evolution</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          title: {
                            display: true,
                            text: 'Year'
                          }
                        },
                        y: {
                          title: {
                            display: true,
                            text: 'Meaning'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Semantic Evolution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Semantic Evolution Timeline</h4>
              <div className="space-y-4">
                {results.meaningData.map((item, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{item.year}</span>
                      <span className="font-medium">{item.meaning}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="how-to-use-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Use</h2>
        <div className="space-y-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 1: Enter a Word</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">Type any word in the input field at the top of the calculator.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">The word can be in any language, but English words will provide the most accurate results.</p>
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 2: Analyze the Word</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">Click the "Analyze Semantic Change" button to generate the analysis.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">The calculator will generate a comprehensive analysis showing how the word's usage and meaning have evolved over time.</p>
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Step 3: Interpret the Results</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <span className="text-green-500">1.</span>
                <p className="text-gray-700">View the usage frequency chart to see how often the word has been used over different time periods.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">2.</span>
                <p className="text-gray-700">Examine the meaning evolution chart to understand how the word's meaning has changed over time.</p>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">3.</span>
                <p className="text-gray-700">Review the semantic evolution timeline for a detailed breakdown of meaning changes in different years.</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      
      <div className="faq-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
        <div className="faq-item bg-white p-6 rounded-xl shadow-md mb-4">
          <h3 className="text-xl font-semibold mb-2">What is Semantic Change?</h3>
          <p className="text-gray-700">
            Semantic change, also known as semantic shift or semantic drift, is the evolution of word usage and meaning over time.
          </p>
        </div>
        <div className="faq-item bg-white p-6 rounded-xl shadow-md mb-4">
          <h3 className="text-xl font-semibold mb-2">How does this calculator work?</h3>
          <p className="text-gray-700">
            This calculator analyzes historical data about word usage and meaning to show how words have evolved over centuries.
          </p>
        </div>
        <div className="faq-item bg-white p-6 rounded-xl shadow-md mb-4">
          <h3 className="text-xl font-semibold mb-2">What can I learn from this analysis?</h3>
          <p className="text-gray-700">
            You can learn about the historical context of words, how their meanings have changed, and how cultural shifts have influenced language.
          </p>
        </div>
      </div>

      <div className="benefits-section mt-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Benefits</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-blue-600 mb-4">📊</div>
            <h3 className="text-xl font-semibold mb-2">Visual Analysis</h3>
            <p className="text-gray-700">Get clear, visual representations of word usage and meaning evolution through interactive charts.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-green-600 mb-4">📚</div>
            <h3 className="text-xl font-semibold mb-2">Historical Context</h3>
            <p className="text-gray-700">Understand how words have evolved over centuries and how cultural shifts have influenced language.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-purple-600 mb-4">💡</div>
            <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
            <p className="text-gray-700">Perfect for students, linguists, and anyone interested in language evolution and semantic change.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-yellow-600 mb-4">🔄</div>
            <h3 className="text-xl font-semibold mb-2">Dynamic Analysis</h3>
            <p className="text-gray-700">Analyze any word instantly and get unique, contextually relevant results every time.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-red-600 mb-4">🎯</div>
            <h3 className="text-xl font-semibold mb-2">Comprehensive Insights</h3>
            <p className="text-gray-700">Get detailed information about usage patterns, meaning evolution, and historical context.</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-4xl text-indigo-600 mb-4">🔄</div>
            <h3 className="text-xl font-semibold mb-2">Easy to Use</h3>
            <p className="text-gray-700">Simple interface that anyone can use - just enter a word and get instant results.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemanticChangeCalculator;